/**
 * Math Blitz: level params and algorithmic problem generation (no content API).
 * Spec: levels 1–2 add 1-digit; 3–4 add/sub 1–20; 5–6 add/sub/simple mult;
 * 7–8 all ops up to 50; 9–10 multi-step (A+B)×C up to 100. Times slightly eased on lower bands.
 */

export interface LevelParams {
  timePerProblemMs: number;
  /** 1–2: add; 3–4: add,sub; 5–6: add,sub,multSimple; 7–8: add,sub,mult,div; 9–10: multiStep */
  mode: "add" | "addSub" | "addSubMultSimple" | "allOps" | "multiStep";
  maxOperand: number;
  /** for mult simple: only ×2, ×5, ×10 */
  multFactors?: number[];
}

export interface MathProblem {
  question: string;
  correctAnswer: number;
  options: number[];
}

/** Seeded RNG in [0, 1) for deterministic problems */
function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

export function getLevelParams(level: number): LevelParams {
  const clamped = Math.max(1, Math.min(10, Math.round(level)));
  if (clamped <= 2) {
    return { timePerProblemMs: 13500, mode: "add", maxOperand: 9 };
  }
  if (clamped <= 4) {
    return { timePerProblemMs: 13500, mode: "addSub", maxOperand: 20 };
  }
  if (clamped <= 6) {
    return {
      timePerProblemMs: 13500,
      mode: "addSubMultSimple",
      maxOperand: 20,
      multFactors: [2, 5, 10],
    };
  }
  if (clamped <= 8) {
    return { timePerProblemMs: 13500, mode: "allOps", maxOperand: 50 };
  }
  return { timePerProblemMs: 13500, mode: "multiStep", maxOperand: 100 };
}

function pickInt(rng: () => number, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/** Build distractors: correct±1, ±2, ±10, random in range; dedupe and shuffle. */
function buildOptions(rng: () => number, correct: number, params: LevelParams): number[] {
  const minVal = params.mode === "multiStep" ? 0 : -params.maxOperand * 2;
  const maxVal = params.mode === "multiStep" ? 300 : params.maxOperand * 2;
  const candidates = new Set<number>([correct]);
  const nearMisses = [
    correct + 1,
    correct - 1,
    correct + 2,
    correct - 2,
    correct + 10,
    correct - 10,
  ].filter((n) => n !== correct && n >= minVal && n <= maxVal);
  for (const n of nearMisses) {
    candidates.add(n);
    if (candidates.size >= 4) break;
  }
  while (candidates.size < 4) {
    const r = pickInt(rng, Math.max(minVal, correct - 15), Math.min(maxVal, correct + 15));
    if (r !== correct) candidates.add(r);
  }
  const arr = Array.from(candidates);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function generateProblem(
  level: number,
  seed: string,
  problemIndex: number
): MathProblem {
  const params = getLevelParams(level);
  const rng = seededRandom(`${seed}-${problemIndex}`);
  const pick = (min: number, max: number) => pickInt(rng, min, max);

  let question: string;
  let correctAnswer: number;

  switch (params.mode) {
    case "add": {
      const a = pick(1, params.maxOperand);
      const b = pick(1, params.maxOperand);
      correctAnswer = a + b;
      question = `${a} + ${b}`;
      break;
    }
    case "addSub": {
      const op = rng() < 0.5 ? "+" : "-";
      if (op === "+") {
        const a = pick(1, params.maxOperand);
        const b = pick(1, params.maxOperand);
        correctAnswer = a + b;
        question = `${a} + ${b}`;
      } else {
        const a = pick(1, params.maxOperand);
        const b = pick(1, a);
        correctAnswer = a - b;
        question = `${a} − ${b}`;
      }
      break;
    }
    case "addSubMultSimple": {
      const which = Math.floor(rng() * 3);
      const factors = params.multFactors ?? [2, 5, 10];
      if (which === 0) {
        const a = pick(1, params.maxOperand);
        const b = pick(1, params.maxOperand);
        correctAnswer = a + b;
        question = `${a} + ${b}`;
      } else if (which === 1) {
        const a = pick(1, params.maxOperand);
        const b = pick(1, a);
        correctAnswer = a - b;
        question = `${a} − ${b}`;
      } else {
        const factor = factors[Math.floor(rng() * factors.length)];
        const b = pick(1, 12);
        correctAnswer = factor * b;
        question = `${factor} × ${b}`;
      }
      break;
    }
    case "allOps": {
      const which = Math.floor(rng() * 4);
      if (which === 0) {
        const a = pick(1, params.maxOperand);
        const b = pick(1, params.maxOperand);
        correctAnswer = a + b;
        question = `${a} + ${b}`;
      } else if (which === 1) {
        const a = pick(1, params.maxOperand);
        const b = pick(1, a);
        correctAnswer = a - b;
        question = `${a} − ${b}`;
      } else if (which === 2) {
        const a = pick(1, Math.min(10, params.maxOperand));
        const b = pick(1, Math.min(10, params.maxOperand));
        correctAnswer = a * b;
        question = `${a} × ${b}`;
      } else {
        const b = pick(2, Math.min(10, params.maxOperand));
        const correct = pick(1, Math.min(10, Math.floor(params.maxOperand / b)));
        const a = correct * b;
        correctAnswer = correct;
        question = `${a} ÷ ${b}`;
      }
      break;
    }
    case "multiStep": {
      const a = pick(1, Math.min(25, params.maxOperand));
      const b = pick(1, Math.min(25, params.maxOperand));
      const c = pick(2, 10);
      correctAnswer = (a + b) * c;
      question = `(${a} + ${b}) × ${c}`;
      break;
    }
    default:
      correctAnswer = 0;
      question = "0 + 0";
  }

  const options = buildOptions(rng, correctAnswer, params);
  return { question, correctAnswer, options };
}

/**
 * Per-round scoring for Math Blitz.
 *
 * Accuracy denominator is the full round length (`problemsPerRound`) —
 * unanswered/timed-out problems count as errors. `avgReactionMs` only
 * includes problems with a recorded `reactionTime` (typically correct taps
 * and explicit wrong taps; `timedOut` rows have no reaction time).
 */
export interface MathBlitzProblemResult {
  userAnswer: number | null;
  correctAnswer: number;
  reactionTime: number | null;
  timedOut: boolean;
}

export interface MathBlitzRoundMetrics {
  accuracy: number;
  correctCount: number;
  errorCount: number;
  avgReactionMs: number;
  reactionTimeMs: number | null;
}

export function computeMathBlitzRoundMetrics(
  results: MathBlitzProblemResult[],
  problemsPerRound: number,
): MathBlitzRoundMetrics {
  const correctCount = results.filter(
    (r) => !r.timedOut && r.userAnswer === r.correctAnswer,
  ).length;
  const accuracy =
    problemsPerRound > 0 ? correctCount / problemsPerRound : 0;
  const reactionTimes = results
    .map((r) => r.reactionTime)
    .filter((rt): rt is number => rt != null);
  const avgReactionMs =
    reactionTimes.length > 0
      ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
      : 0;
  return {
    accuracy,
    correctCount,
    errorCount: Math.max(0, problemsPerRound - correctCount),
    avgReactionMs,
    reactionTimeMs:
      reactionTimes.length > 0 ? Math.round(avgReactionMs) : null,
  };
}
